import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";

// --- UTILITY FOR ERRORS ---
const getErrorMessage = (error) => error.response?.data?.message || "Operation failed";

// --- THUNKS ---

export const getProfile = createAsyncThunk("users/getProfile", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/profile');
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const getPublicProfile = createAsyncThunk("users/getPublicProfile", async (userId, { rejectWithValue }) => {
  try {
    const res = await api.get(`/users/public-profile/${userId}`);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const getUserById = createAsyncThunk("users/getUserById", async (userId, { rejectWithValue }) => {
  try {
    const res = await api.get(`/users/${userId}`);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const updateProfile = createAsyncThunk("users/updateProfile", async (profileData, { rejectWithValue }) => {
  try {
    const res = await api.put('/users/update-user-profile', profileData);
    console.log("updating profile", profileData)
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, thunkAPI) => {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message);
    }
  }
);

export const updateUserById = createAsyncThunk("users/updateUserById", async ({ userId, profileData }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/users/${userId}`, profileData);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const updateNotificationPreferences = createAsyncThunk("users/updateNotifications", async (preferences, { rejectWithValue }) => {
  try {
    const res = await api.put('/users/notification-preferences', preferences);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const listUsers = createAsyncThunk("users/listUsers", async (queryParams = {}, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => { if (v) params.append(k, v); });
    const res = await api.get(`/users/${params.toString() ? `?${params.toString()}` : ''}`);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const searchUsers = createAsyncThunk("users/searchUsers", async (queryParams, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => { if (v) params.append(k, v); });
    const res = await api.get(`/users/search?${params.toString()}`);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

//
export const fetchNearbyUserRequests = createAsyncThunk("users/fetchNearby", async (coords, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams(coords);
    const res = await api.get(`/users/nearby-users?${params.toString()}`);

    const data = res.data; // unwrap axios response first

    // now check data not res
    if (!data.success && data.canAccept === false) {
      return rejectWithValue({
        canAccept: false,
        reason: data.reason,
        status: data.status,
        dailyCount: data.dailyCount,
        maxDaily: data.maxDaily,
        resetIn: data.resetIn,
        isBanned: data.isBanned
      });
    }

    if (!data.success) {
      return rejectWithValue({
        message: data.message || 'Failed to fetch nearby users',
        canAccept: true
      });
    }

    // console.log("users nearby", data);
    return {
      users: data.users || [],
      count: data.count || 0,
      verificationStatus: data.verificationStatus
    };

  } catch (error) {
    // also handle 403 verification errors that come back as thrown errors
    const errData = error.response?.data;
    if (errData?.canAccept === false) {
      return rejectWithValue({
        canAccept: false,
        reason: errData.reason,
        status: errData.status,
        dailyCount: errData.dailyCount,
        maxDaily: errData.maxDaily,
        resetIn: errData.resetIn,
        isBanned: errData.isBanned
      });
    }
    return rejectWithValue({
      message: error.response?.data?.message || 'Operation failed',
      canAccept: true
    });
  }
});

export const updateUserRole = createAsyncThunk("users/updateUserRole", async ({ userId, role }, { rejectWithValue }) => {
  try {
    const res = await api.patch(`/users/${userId}/role`, { role });
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const updateUserStatus = createAsyncThunk("users/updateUserStatus", async ({ userId, status }, { rejectWithValue }) => {
  try {
    const res = await api.patch(`/users/${userId}/status`, { status });
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const deleteUser = createAsyncThunk("users/deleteUser", async (userId, { rejectWithValue }) => {
  try {
    const res = await api.delete(`/users/${userId}`);
    return { userId, ...res.data };
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const bulkUserAction = createAsyncThunk("users/bulkUserAction", async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/users/bulk/action', data);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const exportUsers = createAsyncThunk("users/exportUsers", async (params, { rejectWithValue }) => {
  try {
    const res = await api.post('/users/export', params);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

// --- NEW LOCATION ACTIONS ---
export const fetchLocations = createAsyncThunk('locations/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/users/locations');
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const addLocation = createAsyncThunk('locations/add', async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/users/update-location', data);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

export const deleteLocation = createAsyncThunk('locations/delete', async (id, { rejectWithValue }) => {
  try {
    const res = await api.delete(`/users/locations/${id}`);
    return res.data;
  } catch (error) { return rejectWithValue(getErrorMessage(error)); }
});

// --- SLICE ---

const userSlice = createSlice({
  name: "users",
  initialState: {
    profile: null,
    users: [],
    savedLocations: [],
    selectedUser: null,
    nearbyUsers: [],
    searchResults: [],
    totalUsers: 0,
    loading: false,
    error: null,
    exportLoading: false,
  },
  reducers: {
    clearUsers(state) { state.users = []; state.searchResults = []; state.error = null; },
    clearError(state) { state.error = null; },
    setSelectedUser(state, action) { state.selectedUser = action.payload; },
    clearNearbyUsers(state) { state.nearbyUsers = []; state.error = null; }
  },
  extraReducers: (builder) => {
    builder
      // Get Profile
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.data?.user || action.payload.user || action.payload.data;
      })

      // Public Profile / User By ID
      .addCase(getPublicProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.data?.user || action.payload.user || action.payload.data;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.data?.user || action.payload.user || action.payload.data;
      });

    builder
      // Update Actions
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.data?.user || action.payload.user || action.payload.data;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserById.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.data?.user || action.payload.user || action.payload.data;
        state.selectedUser = user;
        const idx = state.users.findIndex(u => u._id === user._id);
        if (idx !== -1) state.users[idx] = user;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.data?.user || action.payload.user || action.payload.data;
      });

    builder
      // Lists & Search
      .addCase(listUsers.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.users = resp.users || resp;
        state.totalUsers = resp.total || resp.length;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.searchResults = resp.users || resp;
      });

    builder
      .addCase(fetchNearbyUserRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchNearbyUserRequests.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.nearbyUsers = resp.users || [];
        state.verificationStatus = resp.verificationStatus;
        state.error = null;
      })

      .addCase(fetchNearbyUserRequests.rejected, (state, action) => {
        state.loading = false;
        state.nearbyUsers = [];
        state.error = action.payload?.message || 'Failed to load users';
        state.verificationFailed = action.payload?.canAccept === false;
        state.verificationError = action.payload;
      });

    builder
      // Admin Actions
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.data?.user || action.payload.user || action.payload.data;
        const idx = state.users.findIndex(u => u._id === user._id);
        if (idx !== -1) state.users[idx] = user;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.data?.user || action.payload.user || action.payload.data;
        const idx = state.users.findIndex(u => u._id === user._id);
        if (idx !== -1) state.users[idx] = user;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(u => u._id !== action.payload.userId);
      })
      .addCase(bulkUserAction.fulfilled, (state) => { state.loading = false; })
      .addCase(exportUsers.fulfilled, (state) => { state.exportLoading = false; });

    builder
      // --- LOCATIONS ---
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.savedLocations = resp.locations || resp;
      })
      .addCase(addLocation.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.savedLocations = resp.locations || resp;
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.loading = false;
        const resp = action.payload.data || action.payload;
        state.savedLocations = resp.locations || resp;
      });

    builder
      // Unified Loading Handler
      .addMatcher((action) => action.type.endsWith('/pending'), (state, action) => {
        if (action.type.includes('exportUsers')) state.exportLoading = true;
        else state.loading = true;
        state.error = null;
      })
      // Unified Error Handler
      .addMatcher((action) => action.type.endsWith('/rejected'), (state, action) => {
        state.loading = false;
        state.exportLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUsers, clearError, setSelectedUser, clearNearbyUsers } = userSlice.actions;
export default userSlice.reducer;